import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  QueryList,
  TemplateRef,
  ViewChild }     from '@angular/core';
import { NgForm } from '@angular/forms';

import { WizardStepComponent } from './wizard.step.component';

@Component({
  selector   : 'wor-wizard',
  styles: [ require('./wizard.component.scss') ],
  template: require('./wizard.component.html')
})
export class WizardComponent implements AfterContentInit {
  @ContentChildren(WizardStepComponent) steps !: QueryList<WizardStepComponent>;

  // Company set up asks one or two questions a step, so the narrow dialog suits it
  // and stays the default. A screen that has to show a wide step, such as listing
  // every column in a customer's file against ours, asks for the full page width
  // instead. Nothing else about the wizard differs between the two.
  @Input() isFullWidth : boolean = false;

  @Output() indexChange = new EventEmitter<number>();
  @Output() onChange    = new EventEmitter<any>();

  @ViewChild('content') content !: ElementRef<HTMLElement>;

  //WS-6780. Bindable so a consumer can say which step it is on. Company set up does not
  //bind it and is unaffected: the default is still 0 and the wizard still owns it.
  //The import must, because reaching the step that watches a running import is not
  //something the operator does by pressing anything, it is something the run decides.
  @Input() index = 0;

  //WS-6786. Renders every step at once with no header and no footer, which is what a
  //consumer that is not part of onboarding needs. The Countries screen imports state
  //types through the same component as the import hub, and WS-6786 requires it to be
  //unaffected: a progress bar and a step counter above a country editing form is not a
  //cosmetic difference, it claims the screen is something it is not. Off by default, so
  //company set up and the import hub are untouched.
  @Input() isPlain = false;
  originalFormValue: any;

  get isFirst () : boolean {
    return this.index === 0;
  }

  get isInvalid () : boolean {
    if ( !this.content || ( this.isLast && this.isStepSkippable ) ) {
      return false;
    }

    const controls = this.getControlNames(this.content);

    if ( !this.ngForm || controls.length === 0 ) {
      return false;
    }

    for ( const name of controls ) {
      const control = this.ngForm.controls[ name ];

      if ( control && control.invalid ) {
        return true;
      }
    }

    return false;
  }

  get isLast () : boolean {
    return this.index === this.total - 1;
  }

  get currentStep () : WizardStepComponent | null {
    if ( !this.steps ) {
      return null;
    }

    return this.steps.toArray()[ this.index ] || null;
  }

  //WS-6780. Whether the footer's action is held back.
  //
  //A step that has said nothing about it is judged the way every step was judged before,
  //from the form controls found on it. Only a step that answers canAdvance is judged on
  //its own answer, which is what lets the import hold the action back until a file has
  //actually been read, something no form control on that step describes.
  get isBlocked () : boolean {
    const step = this.currentStep;

    if ( step && step.canAdvance !== null ) {
      return !step.canAdvance;
    }

    return this.isInvalid;
  }

  //WS-6780. True unless a step has said it has no action, so every existing wizard keeps
  //its footer exactly as it is.
  get stepHasAction () : boolean {
    const step = this.currentStep;

    return step ? step.hasAction : true;
  }

  //WS-6780. Empty for every step that has not named an action, which is every step of
  //company set up, and the footer then renders exactly the buttons it always has.
  get stepActionLabel () : string {
    const step = this.currentStep;

    return step ? step.actionLabel : '';
  }
  get isStepSkippable () : boolean {
    return this.steps.toArray()[ this.index ].isSkippable;
  }

  get progress () : number {
    return this.total
      ? Math.round(((this.index + 1) / this.total) * 100)
      : 0;
  }

  get template () : TemplateRef<any> {
    return this.steps.toArray()[ this.index ].template;
  }

  get total () : number {
    return this.steps
      ? this.steps.length
      : 0;
  }

  constructor (
    private changeDetectorRef : ChangeDetectorRef,
    @Optional()
    private ngForm            : NgForm
  ) {}

  ngAfterContentInit () : void {
    setTimeout(() => {
      if ( this.ngForm ) {
        this.originalFormValue = { ...this.ngForm.form.value };
      }
    });
  }

  back () : void {
    if ( this.isFirst ) {
      return;
    }

    const previousIndex = this.index;

    this.index--;

    this.changeDetectorRef.detectChanges();

    //WS-6780. next() has always emitted this and back() never did, so a consumer that
    //bound it was told about only some of the moves and drifted. Nothing binds it today.
    this.indexChange.emit(this.index);

    this.onChange.emit({
      action       : 'back',
      currentIndex : this.index,
      previousIndex: previousIndex
    });
  }

  getControlNames ( element : ElementRef<HTMLElement> ) : Array<string> {
    const names : Array<string> = [];
    const root  : HTMLElement   = element.nativeElement;

    const nodes = root.querySelectorAll<HTMLElement>('[name]');

    nodes.forEach(node => {
      const name = node.getAttribute('name');

      if ( name ) {
        names.push(name);
      }
    });

    return names;
  }

  next ( action = 'next' ) : void {
    if ( this.isLast ) {
      return;
    }

    const previousIndex = this.index;

    this.index++;

    this.indexChange.emit(this.index);

    this.onChange.emit({
      action       : action,
      currentIndex : this.index,
      previousIndex: previousIndex
    });
  }

  skip () : void {
    if ( !this.ngForm || !this.originalFormValue ) {
      this.next('skip');

      return;
    }

    const controls = this.getControlNames(this.content);

    controls.forEach(name => {
      const control = this.ngForm.controls[ name ];

      if ( !control ) {
        return;
      }

      const original = this.originalFormValue[ name ];

      control.reset(original, {
        emitEvent: false
      });

      control.markAsPristine();
      control.markAsUntouched();
    });

    this.next('skip');
  }
}
